import React from 'react';
import styled, {css} from 'styled-components';

import {Icon} from '../../../elements/Icon/Icon';

import {colorTheme} from '../../../mixin/colorTheme';

export const SelectionControl = ({children, radio, checkbox, disabled, label}) => (
    <SelectionControlGroup disabled={disabled}>
        <label>
            {children}
            <SelectionContainer>
                {checkbox && (
                    <span>
                        <SelectionCheck>
                            <Icon name="checkBox" />
                        </SelectionCheck>
                        <SelectionUncheck>
                            <Icon name="checkBoxOutlineBlank" />
                        </SelectionUncheck>
                        <SelectionIndeterminate>
                            <Icon name="indeterminateCheckBox" />
                        </SelectionIndeterminate>
                    </span>
                )}
                {radio && (
                    <span>
                        <SelectionCheck>
                            <Icon name="checkCircle" />
                        </SelectionCheck>
                        <SelectionUncheck>
                            <Icon name="radioButtonUnchecked" />
                        </SelectionUncheck>
                    </span>
                )}
                {label}
            </SelectionContainer>
        </label>
    </SelectionControlGroup>
);

export const SelectionContainer = styled.span`
    display: flex;
    align-items: center;
    gap: var(--space-xsm);

    ${Icon.Wrapper} svg {
        width: var(--text-size-large);
        height: var(--text-size-large);
        padding-bottom: 1px;
    }

    &:active {
        ${Icon.Wrapper} svg {
            transform: scale(0.85);
            transition: 0.3s;
        }
    }
`;
const SelectionCheck = styled.span`
    display: none;

    ${Icon.Wrapper} svg {
        color: var(--primary);
    }
`;
const SelectionUncheck = styled.span`
    display: inline-block;

    ${Icon.Wrapper} svg {
        color: var(--label-text-color);
    }
`;
const SelectionIndeterminate = styled.span`
    display: none;

    ${Icon.Wrapper} svg {
        color: var(--primary);
    }
`;

export const SelectionControlGroup = styled.div`
    input[type='checkbox']:checked {
        margin-bottom: var(--space-md);
    }

    * {
        user-select: none;
        cursor: pointer;
    }

    > label {
        margin: 0;
        display: flex;
    }

    input {
        width: 0;
        height: 0;
        opacity: 0;
        margin: 0;
        padding: 0;
    }

    input[type='checkbox']:checked + ${SelectionContainer}, input[type='radio']:checked + ${SelectionContainer} {
        ${SelectionUncheck} {
            display: none;
        }

        ${SelectionCheck} {
            display: inline-block;
        }

        color: var(--default-text-color);

        ${({disabled}) =>
            !disabled &&
            css`
                &:hover {
                    color: var(--primary);
                }
            `}
    }

    input[type='checkbox']:indeterminate + ${SelectionContainer} {
        ${SelectionUncheck} {
            display: none;
        }

        ${SelectionCheck} {
            display: none;
        }

        ${SelectionIndeterminate} {
            display: inline-block;
        }

        color: var(--default-text-color);

        ${({disabled}) =>
            !disabled &&
            css`
                &:hover {
                    color: var(--primary);
                }
            `}
    }

    input[type='checkbox'] + ${SelectionContainer} {
        color: var(--label-text-color);

        ${({disabled}) =>
            !disabled &&
            css`
                &:hover {
                    color: var(--primary);
                }
            `}
    }

    input[type='radio'] + ${SelectionContainer} {
        ${({disabled}) =>
            !disabled &&
            css`
                &:hover {
                    color: var(--primary);
                }
            `}
    }

    input[type='checkbox']
        + ${SelectionContainer},
        input[type='radio']
        + ${SelectionContainer},
        input[type='checkbox']:checked
        + ${SelectionContainer},
        input[type='radio']:checked
        + ${SelectionContainer} {
        ${SelectionUncheck},
        ${SelectionCheck} {
            @keyframes selectionFadeAnimation {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 1;
                }
            }
            animation-duration: 0.6s;
            animation-name: selectionFadeAnimation;
            animation-timing-function: linear;
        }
    }

    pre {
        margin: 0;
    }

    ${colorTheme({
        '--focus-selected': {dark: 'var(--blue-06)', light: 'var(--primary)'},
    })};

    &:has(input[type='checkbox']:focus),
    &:has(input[type='radio']:focus) {
        ${SelectionContainer} {
            color: var(--primary);

            ${Icon.Wrapper} svg {
                color: var(--primary);
            }
        }
    }

    &:has(input[type='checkbox']:checked:focus),
    &:has(input[type='radio']:checked:focus) {
        ${SelectionContainer} {
            color: var(--focus-selected);

            ${Icon.Wrapper} svg {
                color: var(--focus-selected);
            }
        }
    }

    ${SelectionUncheck},
    ${SelectionCheck} {
        @-moz-document url-prefix() {
            margin-bottom: 1px;
        }
    }

    ${({disabled}) => {
        if (disabled)
            return css`
                input[type='checkbox']
                    + ${SelectionContainer},
                    input[type='radio']
                    + ${SelectionContainer},
                    input[type='checkbox']:checked
                    + ${SelectionContainer},
                    input[type='radio']:checked
                    + ${SelectionContainer} {
                    color: var(--disabled-text-color-200);
                    cursor: not-allowed;
                }

                ${SelectionCheck},
                ${SelectionUncheck} {
                    opacity: 0.5;

                    ${Icon.Wrapper},
                    ${Icon.Wrapper} svg {
                        cursor: not-allowed;
                    }
                }
            `;
    }}
`;
